import React, { useEffect, useState, useMemo } from 'react';
import { Plus, Save, Trash2, Loader2, Trophy, Target, Square as SquareIcon, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

const CATS = [
  { k: 'pulcini-2015', l: 'Pulcini 2015' },
  { k: 'pulcini-2016', l: 'Pulcini 2016' },
  { k: 'primi-calci', l: 'Primi Calci 2017/18' },
];
const inp = 'w-full bg-navy-dark border border-white/20 rounded-lg px-2 py-1.5 text-white text-sm focus:outline-none focus:border-pink-brand';
const lbl = 'text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1 block';
const Card = ({ children, className = '' }) => <div className={`glass-card p-4 ${className}`}>{children}</div>;
const Loader = () => <div className="text-center py-8"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>;

/**
 * Calcola la classifica girone a partire dall'array di partite.
 * match: { girone, squadra1, gol1, squadra2, gol2 }
 * I nomi squadra vengono normalizzati (uppercase + spazi singoli) per evitare
 * righe duplicate dovute a differenze di maiuscole/spazi nei dati.
 */
const normTeam = (s) => String(s || '').trim().toUpperCase().replace(/\s+/g, ' ');

export function computeStandings(matches) {
  const byGirone = {};
  (matches || []).forEach((m) => {
    if (!m || !m.squadra1 || !m.squadra2) return;
    const gi = m.girone || 'A';
    byGirone[gi] = byGirone[gi] || {};
    const k1 = normTeam(m.squadra1);
    const k2 = normTeam(m.squadra2);
    const init = (display) => ({ team: display, G: 0, V: 0, Pa: 0, Ps: 0, GF: 0, GS: 0, DR: 0, PUNTI: 0 });
    const a = (byGirone[gi][k1] = byGirone[gi][k1] || init(k1));
    const b = (byGirone[gi][k2] = byGirone[gi][k2] || init(k2));
    const g1 = Number(m.gol1); const g2 = Number(m.gol2);
    if (!Number.isFinite(g1) || !Number.isFinite(g2)) return;
    a.G += 1; b.G += 1;
    a.GF += g1; a.GS += g2; b.GF += g2; b.GS += g1;
    if (g1 > g2) { a.V += 1; b.Ps += 1; a.PUNTI += 3; }
    else if (g1 < g2) { b.V += 1; a.Ps += 1; b.PUNTI += 3; }
    else { a.Pa += 1; b.Pa += 1; a.PUNTI += 1; b.PUNTI += 1; }
    a.DR = a.GF - a.GS; b.DR = b.GF - b.GS;
  });
  const out = {};
  Object.keys(byGirone).sort().forEach((gi) => {
    out[gi] = Object.values(byGirone[gi]).sort((x, y) => (y.PUNTI - x.PUNTI) || (y.DR - x.DR) || (y.GF - x.GF) || x.team.localeCompare(y.team));
  });
  return out;
}

/**
 * Estrae una "match" {girone, squadra1, gol1, squadra2, gol2} da un evento del calendario.
 * Accetta formati come:
 *   evento: "Girone A - RED LIONS - DIECI PIU'"  risultato: "2-2"
 *   evento: "RED LIONS - DIECI PIU'"             risultato: "2-2"
 *   evento: "Girone B – RS3 ACADEMY – OLIMPIA"   risultato: "4:3"
 *   evento: "1° Girone A - 2° Girone B >>> Girone X"  (semifinale, tag finale ignorato)
 */
export function parseMatchFromEvent(evento, risultato) {
  if (!evento || !risultato) return null;
  // Rimuovi tag "semifinale" tipo ">>> Girone X" per parsare correttamente i team
  const clean = String(evento).replace(/\s*>>>\s*Girone\s+[A-Z0-9]+\s*$/i, '').trim();
  const parts = clean.split(/\s*[-–—]\s*/).map((s) => s.trim()).filter(Boolean);
  let girone = 'A';
  let teams = parts;
  if (parts[0] && /^girone\s+/i.test(parts[0])) {
    girone = parts[0].replace(/^girone\s+/i, '').trim().toUpperCase() || 'A';
    teams = parts.slice(1);
  }
  if (teams.length < 2) return null;
  const sc = String(risultato).match(/(\d+)\s*[-–—:]\s*(\d+)/);
  if (!sc) return null;
  return { girone, squadra1: teams[0], gol1: sc[1], squadra2: teams.slice(1).join(' - '), gol2: sc[2] };
}

/**
 * Costruisce il "contesto di risoluzione" per il calendario:
 *   - standingsByCat[catKey][gironeKey] = array di team ordinati (1°, 2°, …)
 *     usato per risolvere "1° Girone A", "2° Girone B", ecc.
 *   - semifinalsByCat[catKey]['X'] = nome squadra vincitrice della semifinale
 *     con tag ">>> Girone X" (per risolvere "Win. Gir. X").
 * Le partite con tag ">>> Girone X" NON entrano nei gironi (sono spareggi).
 */
export function buildResolveContext(calendario) {
  const matchesByCat = {};
  const semifinalsByCat = {};
  (calendario || []).forEach((day) => {
    const cats = String(day.categoria || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (cats.length === 0) return;
    (day.eventi || []).forEach((e) => {
      const semiTagMatch = String(e.evento || '').match(/>>>\s*Girone\s+([A-Z0-9]+)/i);
      const m = parseMatchFromEvent(e.evento, e.risultato);
      if (!m) return;
      if (semiTagMatch) {
        const tag = semiTagMatch[1].toUpperCase();
        const g1 = Number(m.gol1); const g2 = Number(m.gol2);
        let winner = null;
        if (Number.isFinite(g1) && Number.isFinite(g2)) {
          if (g1 > g2) winner = m.squadra1;
          else if (g2 > g1) winner = m.squadra2;
        }
        if (winner) cats.forEach((cat) => {
          semifinalsByCat[cat] = semifinalsByCat[cat] || {};
          semifinalsByCat[cat][tag] = winner;
        });
        return; // non conteggiare nei gironi
      }
      cats.forEach((cat) => {
        matchesByCat[cat] = matchesByCat[cat] || [];
        matchesByCat[cat].push(m);
      });
    });
  });
  const standingsByCat = {};
  Object.keys(matchesByCat).forEach((cat) => {
    standingsByCat[cat] = computeStandings(matchesByCat[cat]);
  });
  return { standingsByCat, semifinalsByCat, matchesByCat };
}

/**
 * Sostituisce nel testo i placeholder con i nomi delle squadre risolti.
 * Supporta:
 *   - "1° Girone A" → nome del 1° classificato del girone A
 *   - "2° Girone B" → 2° del girone B
 *   - "Win. Gir. X" / "Vincente Gir. X" / "Vincitore Gir. X" → vincitore semifinale X
 */
export function resolveEventText(text, cats, ctx) {
  if (!text || !ctx) return text;
  let out = String(text);
  out = out.replace(/(\d+)°?\s*Girone\s+([A-Z0-9])/gi, (match, pos, girone) => {
    const idx = Number(pos) - 1;
    for (const cat of (cats || [])) {
      const standing = ctx.standingsByCat?.[cat]?.[String(girone).toUpperCase()];
      if (standing && standing[idx]) return standing[idx].team;
    }
    return match;
  });
  out = out.replace(/(?:Win\.?|Vinc\.?(?:ente)?|Vincitore)\s*Gir\.?\s+([A-Z0-9]+)/gi, (match, label) => {
    for (const cat of (cats || [])) {
      const w = ctx.semifinalsByCat?.[cat]?.[String(label).toUpperCase()];
      if (w) return w;
    }
    return match;
  });
  return out;
}

/**
 * Rileva la categoria (chiave canonica) da un testo libero, es. il nome
 * dell'evento "PRIMI CALCI 2017/18" → "primi-calci", "PULCINI 2016" → "pulcini-2016".
 * Restituisce null se non trova match.
 */
export function detectCategoriaFromText(text) {
  const t = String(text || '').toLowerCase();
  if (!t.trim()) return null;
  if (/primi\s*calci|\b201[78]\b/.test(t)) return 'primi-calci';
  if (/pulcini[^a-z]{0,3}2015|\b2015\b/.test(t)) return 'pulcini-2015';
  if (/pulcini[^a-z]{0,3}2016|\b2016\b/.test(t)) return 'pulcini-2016';
  return null;
}

/**
 * Risolve un evento nel contesto della sua giornata, scegliendo la categoria
 * più specifica: prima quella rilevata dal testo dell'evento (es. "PULCINI 2016"
 * dentro un evento), poi le categorie della giornata. Restituisce sempre la
 * stringa risolta (con i nomi reali) o l'originale se non c'è nulla da risolvere.
 */
export function resolveEventInDay(eventoText, day, ctx) {
  if (!eventoText || !ctx) return eventoText;
  const dayCats = String(day?.categoria || '').split(',').map((s) => s.trim()).filter(Boolean);
  const detected = detectCategoriaFromText(eventoText);
  const cats = detected ? [detected, ...dayCats.filter((c) => c !== detected)] : dayCats;
  return resolveEventText(eventoText, cats, ctx);
}

/**
 * Per giornate di finale dove il "giorno" contiene il placeholder ("Win. Gir. X - Win. Gir. Y")
 * e gli eventi sono solo i nomi categoria (es. "PRIMI CALCI 2017/18"),
 * questa funzione restituisce il testo della giornata risolto SOLO per la categoria
 * rilevata dall'evento. Es. per l'evento "PRIMI CALCI 2017/18" risolve "Win. Gir. X"
 * con il vincitore della semifinale Girone X della categoria primi-calci.
 */
export function resolveFinaleHeaderForEvent(dayGiornoText, eventoText, day, ctx) {
  if (!dayGiornoText || !ctx) return null;
  const detected = detectCategoriaFromText(eventoText);
  if (!detected) return null;
  const resolved = resolveEventText(dayGiornoText, [detected], ctx);
  return resolved === dayGiornoText ? null : resolved;
}

const __normTeamKey = (s) => String(s || '').trim().toUpperCase().replace(/\s+/g, ' ');

/**
 * Costruisce la classifica FINALE COMPLETA del torneo per una categoria:
 *   1° = vincitore della finale
 *   2° = sconfitto della finale
 *   3°/4° = perdenti delle semifinali (ordinati per punti girone)
 *   5° → ultimo = squadre eliminate ai gironi, ordinate per punteggio girone
 *
 * Rileva:
 *   - finalista vincitore/sconfitto: cerca nel calendario una giornata della categoria
 *     dove day.giorno contiene "Win. Gir." (placeholder finale), risolve i nomi via ctx
 *     e usa il risultato del primo evento di quella categoria.
 *   - perdenti semifinale: cerca eventi con tag ">>> Girone X" + risultato.
 *
 * Restituisce array [{ pos, team, G, V, Pa, Ps, GF, GS, DR, PUNTI, fase }] oppure null se nessuna partita ancora.
 */
export function buildFinalRanking(catKey, calendario, ctx) {
  if (!ctx || !catKey) return null;
  const groupStandings = ctx.standingsByCat?.[catKey] || {};
  // Flatten gironi: indice per team-key normalizzato
  const allTeams = {};
  Object.entries(groupStandings).forEach(([gir, teams]) => {
    teams.forEach((t) => { allTeams[__normTeamKey(t.team)] = { ...t, girone: gir }; });
  });
  if (Object.keys(allTeams).length === 0) return null;

  // Trova perdenti semifinali (tag ">>> Girone X" nel testo evento)
  const semiLosers = [];
  // Trova finalisti (giornata con day.giorno = "Win. Gir. X - Win. Gir. Y" per la categoria)
  let finalWinner = null, finalLoser = null;

  (calendario || []).forEach((day) => {
    const cats = String(day.categoria || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (!cats.includes(catKey)) return;

    // Eventi semifinali
    (day.eventi || []).forEach((e) => {
      const semiTag = String(e.evento || '').match(/>>>\s*Girone\s+([A-Z0-9]+)/i);
      if (!semiTag || !e.risultato) return;
      const m = parseMatchFromEvent(e.evento, e.risultato);
      if (!m) return;
      const g1 = Number(m.gol1); const g2 = Number(m.gol2);
      if (!Number.isFinite(g1) || !Number.isFinite(g2)) return;
      if (g1 > g2) semiLosers.push(m.squadra2);
      else if (g2 > g1) semiLosers.push(m.squadra1);
    });

    // Giornata finale: day.giorno contiene "Win. Gir." → cerca evento di questa categoria
    const isFinaleDay = /win\.?|vinc/i.test(String(day.giorno || ''));
    if (isFinaleDay) {
      const ev = (day.eventi || []).find((e) => detectCategoriaFromText(e.evento) === catKey);
      if (ev && ev.risultato) {
        const resolvedTeams = resolveEventText(day.giorno, [catKey], ctx);
        const m = parseMatchFromEvent(resolvedTeams, ev.risultato);
        if (m) {
          const g1 = Number(m.gol1); const g2 = Number(m.gol2);
          if (Number.isFinite(g1) && Number.isFinite(g2)) {
            if (g1 > g2) { finalWinner = m.squadra1; finalLoser = m.squadra2; }
            else if (g2 > g1) { finalWinner = m.squadra2; finalLoser = m.squadra1; }
          }
        }
      }
    }
  });

  const ranking = [];
  const used = new Set();
  const pushTeam = (team, fase) => {
    if (!team) return;
    const k = __normTeamKey(team);
    if (used.has(k)) return;
    used.add(k);
    const stats = allTeams[k] || { G: 0, V: 0, Pa: 0, Ps: 0, GF: 0, GS: 0, DR: 0, PUNTI: 0 };
    ranking.push({ pos: ranking.length + 1, team, fase, ...stats });
  };

  pushTeam(finalWinner, 'finale-1');
  pushTeam(finalLoser, 'finale-2');
  // Semifinaliste perdenti ordinate per punti girone
  semiLosers
    .filter((t) => !used.has(__normTeamKey(t)))
    .sort((a, b) => (allTeams[__normTeamKey(b)]?.PUNTI || 0) - (allTeams[__normTeamKey(a)]?.PUNTI || 0))
    .forEach((t) => pushTeam(t, 'semifinale'));
  // Squadre rimanenti dai gironi, ordinate per PUNTI / DR / GF
  Object.values(allTeams)
    .filter((t) => !used.has(__normTeamKey(t.team)))
    .sort((a, b) => (b.PUNTI - a.PUNTI) || (b.DR - a.DR) || (b.GF - a.GF) || a.team.localeCompare(b.team))
    .forEach((t) => pushTeam(t.team, 'gironi'));

  // Se nessuna fase è stata giocata, ritorna null
  if (!finalWinner && semiLosers.length === 0 && Object.values(allTeams).every((t) => t.G === 0)) return null;
  return ranking;
}

export default function TornaiEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const [tab, setTab] = useState(CATS[0].k);
  const [bulkText, setBulkText] = useState('');
  const [calendario, setCalendario] = useState([]);

  useEffect(() => {
    api.getTournament().then(setData).catch(() => setData({}));
    api.getCalendarioFull().then((r) => setCalendario(Array.isArray(r) ? r : [])).catch(() => setCalendario([]));
  }, []);
  if (!data) return <Loader />;

  const cur = data[tab] || { active: false, matches: [], marcatori: [], cartellini: [] };
  const setCur = (next) => setData((d) => ({ ...d, [tab]: { ...cur, ...next } }));

  // Partite AUTO-derivate dal Calendario per la categoria attiva
  const autoMatches = (() => {
    const out = [];
    (calendario || []).forEach((day) => {
      const cats = String(day.categoria || '').split(',').map((s) => s.trim()).filter(Boolean);
      if (!cats.includes(tab)) return;
      (day.eventi || []).forEach((e) => {
        const m = parseMatchFromEvent(e.evento, e.risultato);
        if (m) out.push({ ...m, _info: `${day.giorno || ''} ${e.ora || ''}`.trim() });
      });
    });
    return out;
  })();

  const save = async () => {
    setSaving(tab);
    try { await api.saveTournament({ categoria: tab, ...cur }); toast.success('Salvato'); }
    catch (e) { toast.error(e?.message || 'Errore'); }
    setSaving(null);
  };

  const addMarc = () => setCur({ marcatori: [...cur.marcatori, { squadra: '', calciatore: '', reti: '' }] });
  const updMarc = (i, k, v) => { const next = [...cur.marcatori]; next[i] = { ...next[i], [k]: v }; setCur({ marcatori: next }); };
  const rmMarc = (i) => setCur({ marcatori: cur.marcatori.filter((_, idx) => idx !== i) });
  const importBulk = () => {
    const lines = bulkText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) { toast.error('Incolla prima qualcosa'); return; }
    const parsed = lines.map((line) => {
      const cols = line.includes('\t') ? line.split('\t') : line.split(/[;,]/);
      const [squadra, calciatore, reti] = cols.map((s) => (s || '').trim());
      return { squadra: squadra || '', calciatore: calciatore || '', reti: reti || '' };
    }).filter((r) => r.squadra || r.calciatore);
    setCur({ marcatori: [...cur.marcatori, ...parsed] });
    setBulkText('');
    toast.success(`${parsed.length} marcatori importati`);
  };

  const addCart = () => setCur({ cartellini: [...cur.cartellini, { squadra: '', calciatore: '', tipo: 'giallo', motivo: '' }] });
  const updCart = (i, k, v) => { const next = [...cur.cartellini]; next[i] = { ...next[i], [k]: v }; setCur({ cartellini: next }); };
  const rmCart = (i) => setCur({ cartellini: cur.cartellini.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-4" data-testid="tornai-editor">
      <Card>
        <div className="flex flex-wrap gap-2 mb-2">
          {CATS.map((c) => (
            <button key={c.k} onClick={() => setTab(c.k)} className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest ${tab === c.k ? 'bg-pink-brand text-white' : 'bg-navy-dark text-white/70 border border-white/15'}`}>
              {c.l}
              {data[c.k]?.active && <span className="ml-1 text-[10px] bg-green-500/30 px-1 rounded">ON</span>}
            </button>
          ))}
        </div>
        <button
          onClick={() => setCur({ active: !cur.active })}
          className={`mt-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest w-full ${cur.active ? 'bg-green-500/15 border border-green-500/30 text-green-400' : 'bg-red-500/15 border border-red-500/30 text-red-400'}`}
        >
          {cur.active ? '✓ Pannello tornei ATTIVO per questa categoria — clicca per disattivarlo' : '✕ Pannello tornei DISATTIVATO — clicca per attivarlo'}
        </button>
        <p className="text-white/50 text-[11px] mt-2">Quando attivo, in /classifiche per questa categoria appaiono le tabelle Classifica gironi (auto-calcolata), Marcatori e Ammoniti/Espulsi.</p>
      </Card>

      {/* PARTITE — AUTO dal Calendario (centralizzato) */}
      <Card>
        <h3 className="text-white font-bold flex items-center gap-2 mb-2"><Trophy size={16} className="text-pink-brand" /> Risultati partite (auto dal Calendario) — {autoMatches.length}</h3>
        <div className="bg-pink-brand/10 border border-pink-brand/30 rounded-lg p-3 mb-3">
          <p className="text-white text-xs">
            <strong>Sistema centralizzato:</strong> le partite e i risultati sono presi <strong>automaticamente dal Calendario</strong>.
            Per aggiungere/modificare una partita di questa categoria, vai in <strong>Admin → Calendario</strong> e inserisci un evento con
            formato <code className="bg-navy-dark px-1 rounded">Girone A - SQUADRA1 - SQUADRA2</code> e compila il campo <code className="bg-navy-dark px-1 rounded">Risultato</code> (es. <code className="bg-navy-dark px-1 rounded">2-1</code>).
            La classifica girone qui sotto si ricalcola da sola.
          </p>
        </div>
        {autoMatches.length === 0 ? (
          <p className="text-white/50 text-sm italic">Nessuna partita di questa categoria nel calendario con risultato compilato.</p>
        ) : (
          <div className="space-y-1.5">
            {autoMatches.map((m, i) => (
              <div key={i} className="flex items-center gap-2 bg-navy-dark/60 px-3 py-2 rounded border border-white/10 text-sm">
                <span className="text-pink-brand text-[10px] font-bold uppercase tracking-widest w-12">Gir. {m.girone}</span>
                <span className="text-white flex-1">{m.squadra1} <span className="text-white/40">vs</span> {m.squadra2}</span>
                <span className="bg-pink-brand text-white text-xs font-bold px-2 py-0.5 rounded">{m.gol1}-{m.gol2}</span>
                {m._info && <span className="text-white/40 text-[10px] hidden sm:inline">{m._info}</span>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ANTEPRIMA CLASSIFICA */}
      <Card>
        <h3 className="text-white font-bold mb-2 flex items-center gap-2"><Trophy size={16} className="text-pink-brand" /> Anteprima Classifica gironi (auto-calcolata)</h3>
        <StandingsTables matches={autoMatches} />
      </Card>

      {/* MARCATORI */}
      <Card>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="text-white font-bold flex items-center gap-2"><Target size={16} className="text-pink-brand" /> Classifica marcatori ({cur.marcatori.length})</h3>
          <button onClick={addMarc} className="btn-primary !py-1.5 !px-3 !text-xs"><Plus size={12} /> Marcatore</button>
        </div>

        <div className="bg-navy-dark/60 border border-pink-brand/30 rounded-lg p-3 mb-3">
          <label className={lbl}>📋 Incolla da Excel (3 colonne: Squadra · Calciatore · Reti)</label>
          <textarea
            rows={3}
            className={inp + ' font-mono text-xs'}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder="Incolla qui le righe copiate da Excel (separate da TAB) oppure scrivi 'Squadra; Calciatore; Reti' una per riga"
            data-testid="marcatori-bulk-textarea"
          />
          <button onClick={importBulk} className="btn-primary !py-1.5 !px-3 !text-xs mt-2" data-testid="marcatori-bulk-import"><Plus size={12} /> Importa righe incollate</button>
          <p className="text-white/40 text-[10px] mt-1">Accetta: TAB (Excel/Numbers) · punto e virgola · virgola. Una persona per riga.</p>
        </div>

        <div className="space-y-2">
          {cur.marcatori.map((m, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 bg-navy-dark/60 p-2 rounded border border-white/10">
              <div className="col-span-5"><label className={lbl}>Squadra</label><input className={inp} value={m.squadra || ''} onChange={(e) => updMarc(i, 'squadra', e.target.value)} /></div>
              <div className="col-span-5"><label className={lbl}>Calciatore</label><input className={inp} value={m.calciatore || ''} onChange={(e) => updMarc(i, 'calciatore', e.target.value)} /></div>
              <div className="col-span-1"><label className={lbl}>Reti</label><input type="number" className={inp} value={m.reti ?? ''} onChange={(e) => updMarc(i, 'reti', e.target.value)} /></div>
              <div className="col-span-1 flex items-end justify-end"><button onClick={() => rmMarc(i)} className="p-1 text-white/60 hover:text-red-400"><Trash2 size={14} /></button></div>
            </div>
          ))}
        </div>
      </Card>

      {/* CARTELLINI */}
      <Card>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-white font-bold flex items-center gap-2"><SquareIcon size={16} className="text-yellow-400" /> Ammoniti & Espulsi ({cur.cartellini.length})</h3>
          <button onClick={addCart} className="btn-primary !py-1.5 !px-3 !text-xs"><Plus size={12} /> Cartellino</button>
        </div>
        <div className="space-y-2">
          {cur.cartellini.map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 bg-navy-dark/60 p-2 rounded border border-white/10">
              <div className="col-span-4"><label className={lbl}>Squadra</label><input className={inp} value={c.squadra || ''} onChange={(e) => updCart(i, 'squadra', e.target.value)} /></div>
              <div className="col-span-4"><label className={lbl}>Calciatore</label><input className={inp} value={c.calciatore || ''} onChange={(e) => updCart(i, 'calciatore', e.target.value)} /></div>
              <div className="col-span-2"><label className={lbl}>Tipo</label>
                <select className={inp} value={c.tipo || 'giallo'} onChange={(e) => updCart(i, 'tipo', e.target.value)}>
                  <option value="giallo">Giallo</option>
                  <option value="rosso">Rosso (espulsione)</option>
                </select>
              </div>
              <div className="col-span-1"><label className={lbl}>Motivo</label><input className={inp} value={c.motivo || ''} onChange={(e) => updCart(i, 'motivo', e.target.value)} placeholder="opz." /></div>
              <div className="col-span-1 flex items-end justify-end"><button onClick={() => rmCart(i)} className="p-1 text-white/60 hover:text-red-400"><Trash2 size={14} /></button></div>
            </div>
          ))}
        </div>
      </Card>

      <button onClick={save} disabled={saving === tab} className="btn-primary">
        <Save size={14} /> {saving === tab ? 'Salvo…' : `Salva ${CATS.find((c) => c.k === tab)?.l}`}
      </button>
    </div>
  );
}

export function StandingsTables({ matches }) {
  const standings = useMemo(() => computeStandings(matches), [matches]);
  const gironi = Object.keys(standings);
  if (gironi.length === 0) return <p className="text-white/50 text-sm text-center py-3">Nessuna partita inserita. Aggiungi un risultato per vedere la classifica.</p>;
  return (
    <div className="space-y-4">
      {gironi.map((gi) => (
        <div key={gi} className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-white/60 text-[10px] uppercase tracking-widest border-b border-white/15">
                <th className="px-2 py-2 text-left">Classifica Gir. {gi}</th>
                <th className="px-2 py-2 w-10 text-center" title="Giocate">G</th>
                <th className="px-2 py-2 w-10 text-center" title="Vittorie">V</th>
                <th className="px-2 py-2 w-10 text-center" title="Pareggi">Pa</th>
                <th className="px-2 py-2 w-10 text-center" title="Perse">Ps</th>
                <th className="px-2 py-2 w-12 text-center" title="Gol fatti">G/F</th>
                <th className="px-2 py-2 w-12 text-center" title="Gol subiti">G/S</th>
                <th className="px-2 py-2 w-12 text-center" title="Differenza reti">D/R</th>
                <th className="px-2 py-2 w-14 text-center font-bold text-pink-brand">PUNTI</th>
              </tr>
            </thead>
            <tbody>
              {standings[gi].map((r, idx) => (
                <tr key={r.team} className="border-b border-white/5">
                  <td className="px-2 py-1.5 text-white font-semibold flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-pink-brand/20 border border-pink-brand text-pink-brand text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                    {r.team}
                  </td>
                  <td className="px-2 py-1.5 text-white/80 text-center">{r.G}</td>
                  <td className="px-2 py-1.5 text-white/80 text-center">{r.V}</td>
                  <td className="px-2 py-1.5 text-white/80 text-center">{r.Pa}</td>
                  <td className="px-2 py-1.5 text-white/80 text-center">{r.Ps}</td>
                  <td className="px-2 py-1.5 text-white/80 text-center">{r.GF}</td>
                  <td className="px-2 py-1.5 text-white/80 text-center">{r.GS}</td>
                  <td className="px-2 py-1.5 text-white/80 text-center">{r.DR}</td>
                  <td className="px-2 py-1.5 text-pink-brand text-center font-bold">{r.PUNTI}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
